<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import PageHeader from 'dashboard/components-next/teletel/PageHeader.vue';
import ContentCard from 'dashboard/components-next/teletel/ContentCard.vue';
import LifecycleStageBadge from 'dashboard/components-next/teletel/lifecycle/LifecycleStageBadge.vue';

const { t } = useI18n();
const store = useStore();

const CATEGORIES = ['active', 'won', 'lost'];
const DEFAULT_COLOR = '#64748B';

const showArchived = ref(false);
const editingId = ref(null);
const stagesLoadState = ref('idle');
const form = reactive({
  name: '',
  description: '',
  emoji: '',
  color: DEFAULT_COLOR,
  category: 'active',
  is_default: false,
});

const settings = computed(
  () => store.getters['customerLifecycle/getSettings'] || {}
);
const allStages = computed(
  () => store.getters['customerLifecycle/getStages'] || []
);
const activeStages = computed(() =>
  allStages.value
    .filter(stage => !stage.archived_at)
    .sort((a, b) => a.position - b.position)
);
const archivedStages = computed(() =>
  allStages.value.filter(stage => stage.archived_at)
);
const displayedStages = computed(() =>
  showArchived.value
    ? [...activeStages.value, ...archivedStages.value]
    : activeStages.value
);
const uiFlags = computed(
  () => store.getters['customerLifecycle/getUIFlags'] || {}
);
const isLoadingSettings = computed(() => uiFlags.value.isFetchingSettings);
const isLoadingStages = computed(
  () => stagesLoadState.value === 'loading' || uiFlags.value.isFetchingStages
);
const stagesReady = computed(
  () =>
    stagesLoadState.value === 'loaded' &&
    store.getters['customerLifecycle/areStagesLoaded']
);
const stageMutationBusy = computed(
  () =>
    !stagesReady.value ||
    uiFlags.value.isSavingStage ||
    uiFlags.value.isReordering
);
const canSave = computed(
  () =>
    stagesReady.value && Boolean(form.name.trim()) && !stageMutationBusy.value
);
const editingStage = computed(() =>
  allStages.value.find(stage => stage.id === editingId.value)
);
const isEditingDefault = computed(() =>
  Boolean(editingStage.value?.is_default)
);

watch(
  () => form.category,
  category => {
    if (category !== 'active' && !isEditingDefault.value) {
      form.is_default = false;
    }
  }
);

const resetForm = () => {
  editingId.value = null;
  Object.assign(form, {
    name: '',
    description: '',
    emoji: '',
    color: DEFAULT_COLOR,
    category: 'active',
    is_default: false,
  });
};

const loadStages = async () => {
  stagesLoadState.value = 'loading';
  try {
    await store.dispatch('customerLifecycle/getStages', {
      includeArchived: true,
      force: true,
    });
    stagesLoadState.value = store.getters['customerLifecycle/areStagesLoaded']
      ? 'loaded'
      : 'error';
  } catch (error) {
    stagesLoadState.value = 'error';
    useAlert(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        t('TELETEL_LIFECYCLE.ERROR.LOAD')
    );
  }
};

const load = async () => {
  try {
    const loadedSettings = await store.dispatch(
      'customerLifecycle/getSettings',
      { force: true }
    );
    if (loadedSettings?.available && loadedSettings?.enabled) {
      await loadStages();
    }
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        t('TELETEL_LIFECYCLE.ERROR.LOAD')
    );
  }
};

onMounted(load);

const toggleEnabled = async event => {
  const checkbox = event.target;
  const enabled = event.target.checked;
  try {
    await store.dispatch('customerLifecycle/updateSettings', enabled);
  } catch (error) {
    checkbox.checked = Boolean(settings.value.enabled);
    useAlert(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        t('TELETEL_LIFECYCLE.ERROR.SAVE')
    );
    return;
  }

  useAlert(
    t(
      enabled
        ? 'TELETEL_LIFECYCLE.SETTINGS.ENABLED'
        : 'TELETEL_LIFECYCLE.SETTINGS.DISABLED'
    )
  );
  if (enabled) {
    await loadStages();
  } else {
    resetForm();
    stagesLoadState.value = 'idle';
  }
};

const editStage = stage => {
  if (!stagesReady.value || stageMutationBusy.value) return;
  editingId.value = stage.id;
  Object.assign(form, {
    name: stage.name || '',
    description: stage.description || '',
    emoji: stage.emoji || '',
    color: stage.color || DEFAULT_COLOR,
    category: stage.category || 'active',
    is_default: Boolean(stage.is_default),
  });
};

const stagePayload = () => ({
  name: form.name.trim(),
  description: form.description.trim(),
  emoji: form.emoji.trim(),
  color: form.color,
  category: form.category,
  is_default: form.is_default,
});

const saveStage = async () => {
  if (!stagesReady.value || !canSave.value) return;
  try {
    if (editingId.value) {
      await store.dispatch('customerLifecycle/updateStage', {
        id: editingId.value,
        ...stagePayload(),
      });
    } else {
      await store.dispatch('customerLifecycle/createStage', stagePayload());
    }
    resetForm();
    useAlert(t('TELETEL_LIFECYCLE.STAGES.SAVED'));
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        t('TELETEL_LIFECYCLE.ERROR.SAVE_STAGE')
    );
  }
};

const archiveStage = async stage => {
  if (!stagesReady.value || stageMutationBusy.value) return;
  // eslint-disable-next-line no-alert
  if (
    !window.confirm(
      t('TELETEL_LIFECYCLE.STAGES.ARCHIVE_CONFIRM', { name: stage.name })
    )
  ) {
    return;
  }
  try {
    await store.dispatch('customerLifecycle/archiveStage', stage.id);
    useAlert(t('TELETEL_LIFECYCLE.STAGES.ARCHIVED_MESSAGE'));
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        t('TELETEL_LIFECYCLE.ERROR.ARCHIVE_STAGE')
    );
  }
};

const moveStage = async (stage, offset) => {
  if (!stagesReady.value || stageMutationBusy.value) return;
  const index = activeStages.value.findIndex(item => item.id === stage.id);
  const target = index + offset;
  if (index < 0 || target < 0 || target >= activeStages.value.length) return;

  const ids = activeStages.value.map(item => item.id);
  [ids[index], ids[target]] = [ids[target], ids[index]];
  try {
    await store.dispatch('customerLifecycle/reorderStages', ids);
  } catch (error) {
    await store.dispatch('customerLifecycle/getStages', {
      includeArchived: true,
      force: true,
    });
    useAlert(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        t('TELETEL_LIFECYCLE.ERROR.REORDER')
    );
  }
};
</script>

<template>
  <div class="flex w-full flex-col gap-5">
    <PageHeader
      :title="t('TELETEL_LIFECYCLE.SETTINGS.TITLE')"
      :description="t('TELETEL_LIFECYCLE.SETTINGS.DESCRIPTION')"
    />

    <div
      v-if="isLoadingSettings"
      class="flex min-h-40 items-center justify-center text-sm text-n-slate-11"
    >
      {{ t('TELETEL_LIFECYCLE.LOADING') }}
    </div>

    <ContentCard
      v-else-if="!settings.available"
      :title="t('TELETEL_LIFECYCLE.SETTINGS.UNAVAILABLE_TITLE')"
    >
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('TELETEL_LIFECYCLE.SETTINGS.UNAVAILABLE_DESCRIPTION') }}
      </p>
    </ContentCard>

    <template v-else>
      <ContentCard :title="t('TELETEL_LIFECYCLE.SETTINGS.STATUS_TITLE')">
        <label
          class="flex cursor-pointer items-start justify-between gap-4 rounded-xl border border-n-weak bg-n-alpha-1 p-4"
        >
          <span class="min-w-0">
            <span class="block text-sm font-medium text-n-slate-12">
              {{ t('TELETEL_LIFECYCLE.SETTINGS.ENABLE_LABEL') }}
            </span>
            <span class="mt-1 block text-xs leading-5 text-n-slate-10">
              {{ t('TELETEL_LIFECYCLE.SETTINGS.ENABLE_HINT') }}
            </span>
          </span>
          <input
            type="checkbox"
            class="mt-1 size-4 flex-shrink-0"
            :checked="settings.enabled"
            :disabled="uiFlags.isSavingSettings"
            @change="toggleEnabled"
          />
        </label>
      </ContentCard>

      <template v-if="settings.enabled">
        <ContentCard
          v-if="isLoadingStages"
          :title="t('TELETEL_LIFECYCLE.STAGES.TITLE')"
        >
          <p class="mb-0 text-sm text-n-slate-11" aria-live="polite">
            {{ t('TELETEL_LIFECYCLE.STAGES.LOADING') }}
          </p>
        </ContentCard>

        <ContentCard
          v-else-if="!stagesReady"
          :title="t('TELETEL_LIFECYCLE.STAGES.TITLE')"
        >
          <div
            class="flex flex-wrap items-center justify-between gap-3"
            role="alert"
          >
            <p class="mb-0 text-sm text-n-ruby-11">
              {{ t('TELETEL_LIFECYCLE.STAGES.LOAD_ERROR') }}
            </p>
            <Button
              type="button"
              size="sm"
              variant="faded"
              color="slate"
              :label="t('TELETEL_LIFECYCLE.STAGES.RETRY')"
              :aria-label="t('TELETEL_LIFECYCLE.STAGES.RETRY')"
              @click="loadStages"
            />
          </div>
        </ContentCard>

        <template v-else>
          <ContentCard :title="t('TELETEL_LIFECYCLE.STAGES.TITLE')" flush>
            <div class="flex items-center justify-between gap-3 px-4 py-3">
              <p class="mb-0 text-xs text-n-slate-10">
                {{ t('TELETEL_LIFECYCLE.STAGES.ORDER_HINT') }}
              </p>
              <label
                class="inline-flex items-center gap-2 text-xs text-n-slate-11"
              >
                <input v-model="showArchived" type="checkbox" class="mb-0" />
                {{ t('TELETEL_LIFECYCLE.STAGES.SHOW_ARCHIVED') }}
              </label>
            </div>

            <div class="overflow-x-auto">
              <table class="w-full min-w-[720px] text-sm">
                <thead
                  class="border-y border-n-weak bg-n-alpha-1 text-n-slate-10"
                >
                  <tr>
                    <th class="px-4 py-2 text-start font-medium">
                      {{ t('TELETEL_LIFECYCLE.STAGES.STAGE') }}
                    </th>
                    <th class="px-4 py-2 text-start font-medium">
                      {{ t('TELETEL_LIFECYCLE.STAGES.CATEGORY') }}
                    </th>
                    <th class="px-4 py-2 text-start font-medium">
                      {{ t('TELETEL_LIFECYCLE.STAGES.CONTACTS') }}
                    </th>
                    <th class="px-4 py-2 text-end font-medium">
                      {{ t('TELETEL_LIFECYCLE.STAGES.ACTIONS') }}
                    </th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-n-weak">
                  <tr v-for="(stage, index) in displayedStages" :key="stage.id">
                    <td class="px-4 py-3">
                      <div class="flex min-w-0 items-center gap-2">
                        <div v-if="!stage.archived_at" class="flex flex-col">
                          <button
                            type="button"
                            class="text-n-slate-9 hover:text-n-slate-12 disabled:opacity-30"
                            :disabled="index === 0 || stageMutationBusy"
                            :aria-label="
                              t('TELETEL_LIFECYCLE.STAGES.MOVE_UP', {
                                name: stage.name,
                              })
                            "
                            @click="moveStage(stage, -1)"
                          >
                            <span class="i-lucide-chevron-up size-4" />
                          </button>
                          <button
                            type="button"
                            class="text-n-slate-9 hover:text-n-slate-12 disabled:opacity-30"
                            :disabled="
                              index === activeStages.length - 1 ||
                              stageMutationBusy
                            "
                            :aria-label="
                              t('TELETEL_LIFECYCLE.STAGES.MOVE_DOWN', {
                                name: stage.name,
                              })
                            "
                            @click="moveStage(stage, 1)"
                          >
                            <span class="i-lucide-chevron-down size-4" />
                          </button>
                        </div>
                        <div class="min-w-0">
                          <LifecycleStageBadge :stage="stage" />
                          <p
                            v-if="stage.description"
                            class="mb-0 mt-1 max-w-md truncate text-xs text-n-slate-10"
                          >
                            {{ stage.description }}
                          </p>
                          <span
                            v-if="stage.is_default"
                            class="mt-1 inline-block text-xs text-n-blue-11"
                          >
                            {{ t('TELETEL_LIFECYCLE.STAGES.DEFAULT') }}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td class="px-4 py-3 text-n-slate-11">
                      {{
                        t(
                          `TELETEL_LIFECYCLE.CATEGORIES.${stage.category.toUpperCase()}`
                        )
                      }}
                    </td>
                    <td class="px-4 py-3 text-n-slate-11">
                      {{ stage.contacts_count ?? 0 }}
                    </td>
                    <td class="px-4 py-3 text-end">
                      <button
                        v-if="!stage.archived_at"
                        type="button"
                        class="me-3 text-xs font-medium text-n-blue-11 hover:underline"
                        :disabled="stageMutationBusy"
                        @click="editStage(stage)"
                      >
                        {{ t('TELETEL_LIFECYCLE.STAGES.EDIT') }}
                      </button>
                      <button
                        v-if="!stage.archived_at"
                        type="button"
                        class="text-xs font-medium text-n-ruby-11 hover:underline"
                        :disabled="stageMutationBusy"
                        @click="archiveStage(stage)"
                      >
                        {{ t('TELETEL_LIFECYCLE.STAGES.ARCHIVE') }}
                      </button>
                    </td>
                  </tr>
                  <tr v-if="!displayedStages.length">
                    <td
                      colspan="4"
                      class="px-4 py-8 text-center text-n-slate-10"
                    >
                      {{ t('TELETEL_LIFECYCLE.STAGES.EMPTY') }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </ContentCard>

          <ContentCard
            :title="
              t(
                editingId
                  ? 'TELETEL_LIFECYCLE.FORM.EDIT_TITLE'
                  : 'TELETEL_LIFECYCLE.FORM.NEW_TITLE'
              )
            "
          >
            <form class="grid gap-4" @submit.prevent="saveStage">
              <div class="grid gap-4 sm:grid-cols-[5rem_minmax(0,1fr)]">
                <label class="flex flex-col gap-1 text-sm text-n-slate-11">
                  {{ t('TELETEL_LIFECYCLE.FORM.EMOJI') }}
                  <input
                    v-model="form.emoji"
                    maxlength="8"
                    class="h-10 !mb-0 text-center"
                  />
                </label>
                <label class="flex flex-col gap-1 text-sm text-n-slate-11">
                  {{ t('TELETEL_LIFECYCLE.FORM.NAME') }}
                  <input
                    v-model="form.name"
                    maxlength="80"
                    required
                    class="h-10 !mb-0"
                  />
                </label>
              </div>

              <label class="flex flex-col gap-1 text-sm text-n-slate-11">
                {{ t('TELETEL_LIFECYCLE.FORM.DESCRIPTION') }}
                <textarea
                  v-model="form.description"
                  maxlength="240"
                  rows="2"
                  class="!mb-0"
                />
              </label>

              <div class="grid gap-4 sm:grid-cols-2">
                <label class="flex flex-col gap-1 text-sm text-n-slate-11">
                  {{ t('TELETEL_LIFECYCLE.FORM.CATEGORY') }}
                  <select v-model="form.category" class="h-10 !mb-0 text-start">
                    <option
                      v-for="category in CATEGORIES"
                      :key="category"
                      :value="category"
                      :disabled="isEditingDefault && category !== 'active'"
                    >
                      {{
                        t(
                          `TELETEL_LIFECYCLE.CATEGORIES.${category.toUpperCase()}`
                        )
                      }}
                    </option>
                  </select>
                </label>
                <label class="flex flex-col gap-1 text-sm text-n-slate-11">
                  {{ t('TELETEL_LIFECYCLE.FORM.COLOR') }}
                  <input
                    v-model="form.color"
                    type="color"
                    class="h-10 w-full !mb-0 p-1"
                  />
                </label>
              </div>

              <label
                class="inline-flex items-center gap-2 text-sm text-n-slate-11"
              >
                <input
                  v-model="form.is_default"
                  type="checkbox"
                  class="mb-0"
                  :disabled="form.category !== 'active' || isEditingDefault"
                />
                {{ t('TELETEL_LIFECYCLE.FORM.DEFAULT') }}
              </label>

              <div class="flex flex-wrap gap-2">
                <Button
                  type="submit"
                  size="sm"
                  :label="t('TELETEL_LIFECYCLE.FORM.SAVE')"
                  :is-loading="uiFlags.isSavingStage"
                  :disabled="!canSave"
                />
                <Button
                  v-if="editingId"
                  type="button"
                  size="sm"
                  variant="faded"
                  color="slate"
                  :label="t('TELETEL_LIFECYCLE.FORM.CANCEL')"
                  :disabled="stageMutationBusy"
                  @click="resetForm"
                />
              </div>
            </form>
          </ContentCard>
        </template>
      </template>
    </template>
  </div>
</template>
